import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, BarChart3, MessageSquareText, MousePointerClick, Inbox, BadgeCheck, DollarSign, Hourglass, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const PERIODS = [["day", "Today"], ["week", "Week"], ["month", "Month"], ["quarter", "Quarter"], ["year", "Year"], ["all", "All Time"]];

const Stat = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4">
    <div className="flex items-center gap-2 text-white/45 text-[11px] uppercase tracking-wider"><Icon size={13} className="text-[#F2B705]" /> {label}</div>
    <div className={`font-display font-black text-2xl mt-1 ${accent || ""}`}>{value}</div>
    {sub && <div className="text-white/40 text-[11px] mt-0.5">{sub}</div>}
  </div>
);

const SegCard = ({ title, seg, testid, extra }) => (
  <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 space-y-2">
    <div className="font-display font-bold text-base text-[#F2B705]">{title}</div>
    <div className="grid grid-cols-3 gap-2 text-sm">
      <div><div className="text-white/40 text-[11px]">Requests</div><div className="font-bold">{seg.requests}</div></div>
      <div><div className="text-white/40 text-[11px]">Pending</div><div className="font-bold text-amber-300">{seg.pending}</div></div>
      <div><div className="text-white/40 text-[11px]">Approved</div><div className="font-bold text-emerald-400">{seg.approved}</div></div>
      <div><div className="text-white/40 text-[11px]">Declined</div><div className="font-bold text-red-400">{seg.declined}</div></div>
      <div><div className="text-white/40 text-[11px]">Paid in full</div><div className="font-bold">{seg.paid_in_full}</div></div>
      <div><div className="text-white/40 text-[11px]">Completed</div><div className="font-bold">{seg.completed}</div></div>
      {extra}
    </div>
    <div className="border-t border-white/10 pt-2 grid grid-cols-3 gap-2 text-sm">
      <div><div className="text-white/40 text-[11px]">Earned revenue</div><div className="font-bold text-emerald-400">{money(seg.revenue_collected)}</div></div>
      <div><div className="text-white/40 text-[11px]">Pending revenue</div><div className="font-bold text-amber-300">{money(seg.revenue_pending)}</div></div>
      <div><div className="text-white/40 text-[11px]">Avg / approved</div><div className="font-bold">{money(seg.avg_per_approved)}</div></div>
    </div>
  </div>
);

export default function RebookAnalytics({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback((p) => {
    setData(null);
    axios.get(`${API}/rebooking/analytics?period=${p}`, auth).then((r) => setData(r.data))
      .catch(() => toast.error("Could not load analytics."));
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { load(period); }, [period, load]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/rebooking/analytics/export?period=${period}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rebooking_analytics_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4" data-testid="rebook-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map(([k, lbl]) => (
            <button key={k} data-testid={`analytics-period-${k}`} onClick={() => setPeriod(k)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${period === k ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:bg-white/5"}`}>{lbl}</button>
          ))}
        </div>
        <button data-testid="analytics-download" onClick={download} disabled={downloading}
          className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md border border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705]/10 disabled:opacity-60">
          {downloading ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} Download Report (CSV)
        </button>
      </div>

      {!data ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#F2B705]" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            <Stat icon={MessageSquareText} label="Texts sent" value={data.texts.total} testid="analytics-texts" />
            <Stat icon={MousePointerClick} label="Link clicks" value={data.clicks.total} testid="analytics-clicks" />
            <Stat icon={Inbox} label="Requests" value={data.combined.requests} testid="analytics-requests" />
            <Stat icon={BadgeCheck} label="Approved" value={data.combined.approved} testid="analytics-approved" />
            <Stat icon={DollarSign} label="Earned revenue" value={money(data.combined.revenue_collected)} sub="collected payments only" testid="analytics-earned" accent="text-emerald-400" />
            <Stat icon={Hourglass} label="Pending revenue" value={money(data.combined.revenue_pending)} sub="approved, not yet paid" testid="analytics-pending-rev" accent="text-amber-300" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="analytics-conversion">
            {[["Sent → Request", data.conversion.sent_to_request_pct], ["Sent → Approved", data.conversion.sent_to_approved_pct], ["Approved → Paid", data.conversion.approved_to_paid_pct]].map(([lbl, v]) => (
              <div key={lbl} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-3.5">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-white/45"><span className="flex items-center gap-1.5"><TrendingUp size={12} className="text-[#F2B705]" /> {lbl}</span><span className="text-white font-bold text-sm">{v}%</span></div>
                <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden"><div className="h-full bg-[#F2B705] rounded-full" style={{ width: `${Math.min(100, v)}%` }} /></div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            <SegCard title="Keep This Vehicle — Extensions" seg={data.extensions} testid="analytics-seg-extensions" />
            <SegCard title="Request Another Vehicle — Rebookings" seg={data.rebookings} testid="analytics-seg-rebookings"
              extra={<><div><div className="text-white/40 text-[11px]">Offers sent</div><div className="font-bold">{data.rebookings.offers_sent}</div></div>
                <div><div className="text-white/40 text-[11px]">Offers declined</div><div className="font-bold">{data.rebookings.offers_declined}</div></div></>} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid="analytics-by-source">
              <div className="font-display font-bold text-sm mb-2 flex items-center gap-2"><BarChart3 size={15} className="text-[#F2B705]" /> Where requests came from</div>
              {data.by_source.length === 0 ? <p className="text-white/40 text-xs">No requests in this period.</p> : (
                <table className="w-full text-xs">
                  <thead><tr className="text-white/40 text-left"><th className="pb-1.5 font-medium">Source</th><th className="pb-1.5 font-medium">Requests</th><th className="pb-1.5 font-medium">Approved</th><th className="pb-1.5 font-medium text-right">Earned</th></tr></thead>
                  <tbody>{data.by_source.map((s) => (
                    <tr key={s.source} className="border-t border-white/5"><td className="py-1.5">{s.source}</td><td>{s.requests}</td><td>{s.approved}</td><td className="text-right text-emerald-400 font-semibold">{money(s.revenue_collected)}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid="analytics-channels">
              <div className="font-display font-bold text-sm mb-2 flex items-center gap-2"><MessageSquareText size={15} className="text-[#F2B705]" /> Texts &amp; clicks by channel</div>
              <table className="w-full text-xs">
                <thead><tr className="text-white/40 text-left"><th className="pb-1.5 font-medium">Channel</th><th className="pb-1.5 font-medium">Texts sent</th><th className="pb-1.5 font-medium text-right">Link clicks</th></tr></thead>
                <tbody>{Object.entries(data.texts.by_channel).map(([k, v]) => (
                  <tr key={k} className="border-t border-white/5"><td className="py-1.5">{k}</td><td>{v}</td><td className="text-right">{data.clicks.by_channel[k] || 0}</td></tr>
                ))}
                {Object.entries(data.clicks.by_channel).filter(([k]) => !(k in data.texts.by_channel)).map(([k, v]) => (
                  <tr key={k} className="border-t border-white/5"><td className="py-1.5">{k}</td><td>—</td><td className="text-right">{v}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <p className="text-white/30 text-[11px]">Earned revenue counts collected payments only. Approved-but-unpaid amounts show as pending revenue. Extension payments are attributed after the original rental balance is covered.</p>
        </>
      )}
    </div>
  );
}
